import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { colors, fontFamily } from '../../../theme/tikabTokens';

// ── Presentationstema ─────────────────────────────────────────────────────────

export const DEFAULT_SLIDE_THEME = {
  panelStyle:   'glass',  // 'glass' | 'opaque'
  borderRadius: 14,       // px: 0, 8, 14, 24
  panelOpacity: 52,       // 0–100 → alpha för glasspanelen
  panelBlur:    24,       // px → backdrop-filter blur
  showQr:       false,    // visa QR-kod på alla slides
  logoPosX:     36,       // px från höger kant för logotypen
  // Referensprojekt — globala synlighetsval
  refShowRole:          false,
  refShowDescription:   true,
  refShowOurAssignment: true,
  refShowBudget:        false,
  refShowTime:          false,
  refShowChips:         true,
};

// slideOverride = { panelOpacity, panelBlur } — slide-nivå åsidosätter global tema
function getPanelStyles(slideTheme, variant = 'dark', slideOverride = {}) {
  const th      = { ...DEFAULT_SLIDE_THEME, ...slideTheme };
  if (slideOverride.panelOpacity !== undefined) th.panelOpacity = slideOverride.panelOpacity;
  if (slideOverride.panelBlur    !== undefined) th.panelBlur    = slideOverride.panelBlur;
  const isGlass = th.panelStyle === 'glass';
  const a       = Math.min(1, Math.max(0, (th.panelOpacity ?? 52) / 100));
  const blur    = th.panelBlur ?? 24;

  // Mörkt: 10,6,2 | Ljust: 242,237,228
  const base    = variant === 'dark' ? '10,6,2' : '242,237,228';
  // Opaque-läge: klämmer alpha uppåt
  const alpha   = isGlass ? a : Math.min(1, a + 0.40);

  return {
    background: `rgba(${base},${alpha.toFixed(2)})`,
    backdropFilter: isGlass ? `blur(${blur}px) saturate(180%)` : 'none',
    WebkitBackdropFilter: isGlass ? `blur(${blur}px) saturate(180%)` : 'none',
    border: `1px solid ${isGlass ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.05)'}`,
    borderRadius: `${th.borderRadius}px`,
  };
}

// ── Videocache ─────────────────────────────────────────────────────────────────

const videoCache = new Map();

function useCachedVideo(url) {
  const [blobUrl, setBlobUrl] = useState('');
  useEffect(() => {
    if (!url) { setBlobUrl(''); return; }
    if (videoCache.has(url)) { setBlobUrl(videoCache.get(url)); return; }
    let cancelled = false;
    fetch(url)
      .then(r => r.blob())
      .then(blob => {
        if (cancelled) return;
        const b = URL.createObjectURL(blob);
        videoCache.set(url, b);
        setBlobUrl(b);
      })
      .catch(() => { if (!cancelled) setBlobUrl(url); });
    return () => { cancelled = true; };
  }, [url]);
  return blobUrl;
}

// ── Slide-skala ───────────────────────────────────────────────────────────────

export function useSlideScale(containerRef, fitHeight = false) {
  const [scale, setScale] = useState(0); // 0 = not yet measured → hide until correct
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (fitHeight && height > 0) {
        setScale(Math.min(width / 1280, height / 720));
      } else {
        setScale(width / 1280);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [containerRef, fitHeight]);
  return scale;
}

// ── Auto-textskalning ─────────────────────────────────────────────────────────
// Minskar font-size tills innehållet ryms i sin container.

function useFitText(ref, deps, maxSize, minSize = 10) {
  const [size, setSize] = useState(maxSize);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    let s = maxSize;
    el.style.fontSize = s + 'px';
    while (s > minSize && el.scrollHeight > el.offsetHeight + 2) {
      s -= 1;
      el.style.fontSize = s + 'px';
    }
    setSize(s);
    el.style.fontSize = '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxSize, minSize, ...deps]);
  return size;
}

// ── Bakgrundsbild / video ─────────────────────────────────────────────────────

function BgImage({ url, videoUrl, theme = 'dark' }) {
  const cachedVideo = useCachedVideo(videoUrl);
  return (
    <>
      <Box sx={{
        position: 'absolute', inset: 0,
        bgcolor: theme === 'light' ? colors.cream : colors.black,
      }} />
      {videoUrl ? (
        <Box
          component="video"
          src={cachedVideo || videoUrl}
          autoPlay muted loop playsInline preload="auto"
          sx={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%',
            objectFit: 'cover', objectPosition: 'center', display: 'block',
          }}
        />
      ) : url ? (
        <Box sx={{
          position: 'absolute', inset: 0,
          backgroundImage: `url("${url}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }} />
      ) : null}
    </>
  );
}

// ── Tikab-logotyp ─────────────────────────────────────────────────────────────

function TikabLogo({ light = true, altLogoUrl, qrUrl, qrLogoUrl, qrSize = 72, logoPosX = 36 }) {
  // QR hamnar uppe till höger med fast position — oberoende av logoPosX
  const qrBlock = qrUrl ? (
    <Box sx={{
      position: 'absolute', top: 28, right: 36, zIndex: 10,
      // Liten bakgrund säkerställer att inbäddad logotyp alltid syns
      bgcolor: light ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.85)',
      borderRadius: '6px', p: '4px',
      display: 'inline-flex',
    }}>
      <QRCodeSVG
        value={qrUrl}
        size={qrSize}
        level="H"
        bgColor="transparent"
        fgColor={light ? 'rgba(255,255,255,0.92)' : 'rgba(20,10,5,0.88)'}
        imageSettings={qrLogoUrl ? {
          src: qrLogoUrl,
          width: Math.round(qrSize * 0.26),
          height: Math.round(qrSize * 0.26),
          excavate: true,
        } : undefined}
      />
    </Box>
  ) : null;

  if (altLogoUrl) {
    return (<>
      {qrBlock}
      <Box sx={{ position: 'absolute', bottom: 18, right: logoPosX, zIndex: 10 }}>
        <Box component="img" src={altLogoUrl} alt="Logotyp" sx={{
          height: 44, maxWidth: 220, objectFit: 'contain',
          display: 'block', opacity: 0.88,
        }} />
      </Box>
    </>);
  }
  return (<>
    {qrBlock}
    <Box sx={{
      position: 'absolute', bottom: 22, right: logoPosX + 4, zIndex: 10,
      color: light ? 'rgba(255,255,255,0.85)' : colors.darkText,
      fontSize: 22, fontWeight: 700, letterSpacing: '0.04em',
      fontFamily, userSelect: 'none',
    }}>
      tikab
    </Box>
  </>);
}

// ── Sektionsetikett ───────────────────────────────────────────────────────────

function Label({ children, sx = {} }) {
  return (
    <Typography sx={{
      fontFamily, fontSize: 18, fontWeight: 700,
      letterSpacing: '0.18em', textTransform: 'uppercase',
      color: colors.orange, mb: 2, ...sx,
    }}>
      {children}
    </Typography>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Slide-komponenter (1280 × 720)
// ══════════════════════════════════════════════════════════════════════════════

// ── 1. cover ──────────────────────────────────────────────────────────────────

function CoverSlide({ slide, bgUrl, videoUrl, altLogoUrl, qrUrl, qrLogoUrl, logoPosX = 36, textScale = 1 }) {
  return (
    <Box sx={{ width: 1280, height: 720, position: 'relative', overflow: 'hidden', fontFamily }}>
      <BgImage url={bgUrl} videoUrl={videoUrl} theme={slide.theme || 'dark'} />
      <Box sx={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(90deg, rgba(0,0,0,0.58) 0%, rgba(0,0,0,0.12) 65%, transparent 100%)',
      }} />
      <Box sx={{
        position: 'absolute', left: 72, top: 0, bottom: 0,
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        maxWidth: 680,
      }}>
        {slide.tagline && (
          <Typography sx={{
            fontFamily, fontSize: Math.round((slide.fontSize ?? 28) * 0.57 * textScale), fontWeight: 600,
            letterSpacing: '0.2em', textTransform: 'uppercase',
            color: colors.orange, mb: 2,
          }}>
            {slide.tagline}
          </Typography>
        )}
        <Typography sx={{
          fontFamily, fontSize: Math.round((slide.title?.length > 40 ? 58 : 72) * textScale),
          fontWeight: 800, color: colors.white, lineHeight: 1.05,
          mb: slide.client ? 3 : 0,
        }}>
          {slide.title || 'Rubrik'}
        </Typography>
        {slide.client && (
          <Typography sx={{
            fontFamily, fontSize: Math.round((slide.fontSize ?? 28) * textScale), fontWeight: 400,
            color: 'rgba(255,255,255,0.75)',
          }}>
            {slide.client}
          </Typography>
        )}
        {slide.date && (
          <Typography sx={{
            fontFamily, fontSize: Math.round((slide.fontSize ?? 28) * 0.72 * textScale), fontWeight: 400,
            color: 'rgba(255,255,255,0.55)', mt: slide.client ? 1.5 : 3,
            letterSpacing: '0.05em',
          }}>
            {slide.date}
          </Typography>
        )}
        {(slide.presenters?.length > 0) && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', mt: 3 }}>
            {slide.presenters.map((name, i) => (
              <Box key={i} sx={{
                px: '14px', py: '5px',
                bgcolor: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.25)',
                borderRadius: '20px',
                fontFamily, fontSize: 16, fontWeight: 500,
                color: 'rgba(255,255,255,0.88)',
                letterSpacing: '0.02em',
              }}>
                {name}
              </Box>
            ))}
          </Box>
        )}
      </Box>
      <TikabLogo light altLogoUrl={altLogoUrl} qrUrl={qrUrl} qrLogoUrl={qrLogoUrl} logoPosX={logoPosX} />
    </Box>
  );
}

// ── 1b. hero ──────────────────────────────────────────────────────────────────

function HeroSlide({ slide, bgUrl, videoUrl, altLogoUrl, qrUrl, qrLogoUrl, textScale = 1 }) {
  const opacity = (slide.overlayOpacity ?? 50) / 100;
  const blur    = slide.overlayBlur ?? 0;
  // Gradient: full opacity on left → transparent at ~55% of width
  const gradientWidth = slide.overlayWidth ?? 55;
  const gradient = `linear-gradient(90deg, rgba(0,0,0,${opacity.toFixed(2)}) 0%, rgba(0,0,0,0) ${gradientWidth}%)`;
  const effectiveQrUrl = slide.qrTarget === 'custom' ? (slide.qrCustomUrl || '') : qrUrl;

  return (
    <Box sx={{ width: 1280, height: 720, position: 'relative', overflow: 'hidden', fontFamily }}>
      <BgImage url={bgUrl} videoUrl={videoUrl} theme={slide.theme || 'dark'} />

      {/* Blur-lager — klipt till vänster halvan med CSS mask */}
      {blur > 0 && (
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 1,
          backdropFilter: `blur(${blur}px)`,
          WebkitBackdropFilter: `blur(${blur}px)`,
          WebkitMaskImage: 'linear-gradient(90deg, black 0%, black 30%, transparent 55%)',
          maskImage: 'linear-gradient(90deg, black 0%, black 30%, transparent 55%)',
        }} />
      )}

      {/* Gradient-övertäckning för läsbarhet */}
      {opacity > 0 && (
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 1, background: gradient }} />
      )}

      {/* Textinnehåll */}
      <Box sx={{
        position: 'absolute', left: 72, top: 0, bottom: 0, zIndex: 2,
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        maxWidth: slide.textBlockWidth ?? 700,
      }}>
        {altLogoUrl && (
          <Box component="img" src={altLogoUrl} alt="Logotyp" sx={{
            maxWidth: 576, maxHeight: 240, objectFit: 'contain',
            objectPosition: 'left center', display: 'block',
            mb: slide.title ? 5 : 0,
            transform: `translate(${slide.logoOffsetX ?? 0}px, ${slide.logoOffsetY ?? 0}px)`,
          }} />
        )}
        {slide.title && (
          <Typography sx={{
            fontFamily, fontSize: Math.round((slide.titleFontSize ?? (slide.title.length > 40 ? 52 : 64)) * textScale),
            fontWeight: 800, color: colors.white, lineHeight: 1.08,
            mb: (slide.tagline || slide.client) ? 2.5 : 0,
          }}>
            {slide.title}
          </Typography>
        )}
        {slide.tagline && (
          <Typography sx={{
            fontFamily, fontSize: Math.round(22 * textScale), fontWeight: 600,
            letterSpacing: '0.1em', textTransform: 'uppercase',
            color: colors.orange, mb: slide.client ? 1.5 : 0,
          }}>
            {slide.tagline}
          </Typography>
        )}
        {slide.client && (
          <Typography sx={{
            fontFamily, fontSize: Math.round(26 * textScale), fontWeight: 400,
            color: 'rgba(255,255,255,0.75)',
          }}>
            {slide.client}
          </Typography>
        )}
      </Box>
      {/* QR nere till höger — dubbel storlek (ingen logo konkurrerar här) */}
      {effectiveQrUrl && (
        <Box sx={{ position: 'absolute', bottom: 28, right: 40, zIndex: 10,
          bgcolor: 'rgba(0,0,0,0.45)', borderRadius: '10px', p: '8px',
          backdropFilter: 'blur(4px)' }}>
          <QRCodeSVG
            value={effectiveQrUrl}
            size={144}
            level="H"
            bgColor="rgba(0,0,0,0)"
            fgColor="rgba(255,255,255,0.88)"
            imageSettings={qrLogoUrl ? {
              src: qrLogoUrl,
              width: 38,
              height: 38,
              excavate: true,
            } : undefined}
          />
        </Box>
      )}
    </Box>
  );
}

// ── 1c. hero-mirror ───────────────────────────────────────────────────────────

function HeroMirrorSlide({ slide, bgUrl, videoUrl, altLogoUrl, qrUrl, qrLogoUrl, textScale = 1 }) {
  const opacity = (slide.overlayOpacity ?? 50) / 100;
  const blur    = slide.overlayBlur ?? 0;
  // Gradient: full opacity on right → transparent at ~55% from right
  const gradientWidth = slide.overlayWidth ?? 55;
  const gradient = `linear-gradient(270deg, rgba(0,0,0,${opacity.toFixed(2)}) 0%, rgba(0,0,0,0) ${gradientWidth}%)`;
  const effectiveQrUrl = slide.qrTarget === 'custom' ? (slide.qrCustomUrl || '') : qrUrl;

  return (
    <Box sx={{ width: 1280, height: 720, position: 'relative', overflow: 'hidden', fontFamily }}>
      <BgImage url={bgUrl} videoUrl={videoUrl} theme={slide.theme || 'dark'} />

      {/* Blur-lager — klipt till höger halvan med CSS mask */}
      {blur > 0 && (
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 1,
          backdropFilter: `blur(${blur}px)`,
          WebkitBackdropFilter: `blur(${blur}px)`,
          WebkitMaskImage: 'linear-gradient(270deg, black 0%, black 30%, transparent 55%)',
          maskImage: 'linear-gradient(270deg, black 0%, black 30%, transparent 55%)',
        }} />
      )}

      {/* Gradient-övertäckning för läsbarhet */}
      {opacity > 0 && (
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 1, background: gradient }} />
      )}

      {/* Textinnehåll — höger sida */}
      <Box sx={{
        position: 'absolute', right: 72, top: 0, bottom: 0, zIndex: 2,
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        maxWidth: slide.textBlockWidth ?? 700, alignItems: 'flex-end', textAlign: 'right',
      }}>
        {altLogoUrl && (
          <Box component="img" src={altLogoUrl} alt="Logotyp" sx={{
            maxWidth: 576, maxHeight: 240, objectFit: 'contain',
            objectPosition: 'right center', display: 'block',
            mb: slide.title ? 5 : 0,
            transform: `translate(${slide.logoOffsetX ?? 0}px, ${slide.logoOffsetY ?? 0}px)`,
          }} />
        )}
        {slide.title && (
          <Typography sx={{
            fontFamily, fontSize: Math.round((slide.titleFontSize ?? (slide.title.length > 40 ? 52 : 64)) * textScale),
            fontWeight: 800, color: colors.white, lineHeight: 1.08,
            mb: (slide.tagline || slide.client) ? 2.5 : 0,
          }}>
            {slide.title}
          </Typography>
        )}
        {slide.tagline && (
          <Typography sx={{
            fontFamily, fontSize: Math.round(22 * textScale), fontWeight: 600,
            letterSpacing: '0.1em', textTransform: 'uppercase',
            color: colors.orange, mb: slide.client ? 1.5 : 0,
          }}>
            {slide.tagline}
          </Typography>
        )}
        {slide.client && (
          <Typography sx={{
            fontFamily, fontSize: Math.round(26 * textScale), fontWeight: 400,
            color: 'rgba(255,255,255,0.75)',
          }}>
            {slide.client}
          </Typography>
        )}
      </Box>

      {/* QR nere till vänster */}
      {effectiveQrUrl && (
        <Box sx={{ position: 'absolute', bottom: 28, left: 40, zIndex: 10,
          bgcolor: 'rgba(0,0,0,0.45)', borderRadius: '10px', p: '8px',
          backdropFilter: 'blur(4px)' }}>
          <QRCodeSVG
            value={effectiveQrUrl}
            size={144}
            level="H"
            bgColor="rgba(0,0,0,0)"
            fgColor="rgba(255,255,255,0.88)"
            imageSettings={qrLogoUrl ? {
              src: qrLogoUrl,
              width: 38,
              height: 38,
              excavate: true,
            } : undefined}
          />
        </Box>
      )}
    </Box>
  );
}

// ── 2. divider ────────────────────────────────────────────────────────────────

function DividerSlide({ slide, bgUrl, videoUrl, altLogoUrl, qrUrl, qrLogoUrl, logoPosX = 36, textScale = 1 }) {
  return (
    <Box sx={{ width: 1280, height: 720, position: 'relative', overflow: 'hidden', fontFamily }}>
      <BgImage url={bgUrl} videoUrl={videoUrl} theme={slide.theme || 'dark'} />
      <Box sx={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(90deg, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.08) 70%, transparent 100%)',
      }} />
      <Box sx={{
        position: 'absolute', left: 72, top: 0, bottom: 0,
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        maxWidth: 680,
      }}>
        <Typography sx={{
          fontFamily, fontSize: Math.round((slide.title?.length > 30 ? 64 : 80) * textScale),
          fontWeight: 800, color: colors.white, lineHeight: 1.05,
        }}>
          {slide.title || 'Sektion'}
        </Typography>
      </Box>
      <TikabLogo light altLogoUrl={altLogoUrl} qrUrl={qrUrl} qrLogoUrl={qrLogoUrl} logoPosX={logoPosX} />
    </Box>
  );
}

// ── 2b. section (centrerat) ───────────────────────────────────────────────────

function SectionSlide({ slide, bgUrl, videoUrl, altLogoUrl, qrUrl, qrLogoUrl, logoPosX = 36, textScale = 1 }) {
  const titleLen  = (slide.title || '').length;
  const fontSize  = Math.round((titleLen > 40 ? 72 : titleLen > 20 ? 96 : 120) * textScale);
  // overlay: 0 = ingen dimma, 100 = helt svart. Default 0 (ingen helskärmsöverläggg)
  const overlayA  = (slide.overlayOpacity ?? 0) / 100;
  // panel bakom text: 0 = ingen skylt, 100 = helt ogenomskinlig
  const panelA    = (slide.panelOpacity ?? 0) / 100;
  const radius    = slide.borderRadius ?? 12;

  return (
    <Box sx={{ width: 1280, height: 720, position: 'relative', overflow: 'hidden', fontFamily }}>
      <BgImage url={bgUrl} videoUrl={videoUrl} theme={slide.theme || 'dark'} />
      {overlayA > 0 && (
        <Box sx={{ position: 'absolute', inset: 0, background: `rgba(0,0,0,${overlayA.toFixed(2)})` }} />
      )}
      <Box sx={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        px: 8, textAlign: 'center', gap: 0,
      }}>
        <Box sx={{
          display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 2,
          px: panelA > 0 ? 6 : 0, py: panelA > 0 ? 4 : 0,
          background: panelA > 0 ? `rgba(0,0,0,${panelA.toFixed(2)})` : 'transparent',
          borderRadius: panelA > 0 ? `${radius}px` : 0,
          backdropFilter: panelA > 0 ? 'blur(12px)' : 'none',
          WebkitBackdropFilter: panelA > 0 ? 'blur(12px)' : 'none',
        }}>
          {slide.label && (<>
            <Typography sx={{
              fontFamily, fontSize: 18, fontWeight: 700,
              color: colors.orange, letterSpacing: '0.2em', textTransform: 'uppercase',
            }}>
              {slide.label}
            </Typography>
            <Box sx={{ width: 60, height: 3, bgcolor: colors.orange, borderRadius: 2 }} />
          </>)}
          <Typography sx={{
            fontFamily, fontSize, fontWeight: 800,
            color: colors.white, lineHeight: 1.05,
          }}>
            {slide.title || 'Avsnitt'}
          </Typography>
        </Box>
      </Box>
      <TikabLogo light altLogoUrl={altLogoUrl} qrUrl={qrUrl} qrLogoUrl={qrLogoUrl} logoPosX={logoPosX} />
    </Box>
  );
}

// ── 3. stats ──────────────────────────────────────────────────────────────────

function StatsSlide({ slide, bgUrl, videoUrl, altLogoUrl, qrUrl, qrLogoUrl, logoPosX = 36, textScale = 1 }) {
  const stats = slide.stats || [];
  const count = stats.length;
  const valueFontSize = Math.round((count <= 1 ? 180 : count === 2 ? 150 : count === 3 ? 140 : count === 4 ? 120 : 84) * textScale);
  const unitFontSize  = Math.round((count <= 1 ? 64  : count === 2 ? 54  : count === 3 ? 50  : count === 4 ? 44 : 32) * textScale);
  const autoLabelSize = count <= 2 ? 24  : count === 3 ? 22  : count === 4 ? 20  : 16;
  const labelFontSize = Math.round((slide.fontSize ?? autoLabelSize) * textScale);
  const autoGap       = count <= 2 ? 100 : count === 3 ? 80  : count === 4 ? 52  : 32;
  const gapPx         = slide.statGap ?? autoGap;
  const useGrid       = count > 4;

  return (
    <Box sx={{ width: 1280, height: 720, position: 'relative', overflow: 'hidden', fontFamily }}>
      <BgImage url={bgUrl} videoUrl={videoUrl} theme="dark" />
      <Box sx={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.38)' }} />
      {slide.label && (
        <Box sx={{ position: 'absolute', top: 56, left: 72 }}>
          <Label>{slide.label}</Label>
        </Box>
      )}
      <Box sx={{
        position: 'absolute', inset: 0,
        display: useGrid ? 'grid' : 'flex',
        ...(useGrid
          ? { gridTemplateColumns: `repeat(${Math.ceil(count / 2)}, 1fr)`, gridTemplateRows: '1fr 1fr', px: '72px', py: '80px' }
          : { alignItems: 'center', justifyContent: 'center', gap: `${gapPx}px`, px: '72px' }),
      }}>
        {stats.map((s, i) => (
          <Box key={i} sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', lineHeight: 1, gap: '0.15em' }}>
              <Typography sx={{ fontFamily, fontSize: valueFontSize, fontWeight: 800, color: colors.white, lineHeight: 1 }}>
                {s.value}
              </Typography>
              {s.unit && (
                <Typography sx={{
                  fontFamily, fontSize: unitFontSize, fontWeight: 700,
                  color: colors.orange, lineHeight: 1, mb: `${unitFontSize * 0.12}px`,
                  letterSpacing: '0.04em',
                }}>
                  {s.unit}
                </Typography>
              )}
            </Box>
            {s.label && (
              <Typography sx={{
                fontFamily, fontSize: labelFontSize, fontWeight: 600,
                color: colors.orange, mt: 0.75, maxWidth: 240, lineHeight: 1.3,
                letterSpacing: '0.06em', textTransform: 'uppercase',
              }}>
                {s.label}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
      <TikabLogo light altLogoUrl={altLogoUrl} qrUrl={qrUrl} qrLogoUrl={qrLogoUrl} logoPosX={logoPosX} />
    </Box>
  );
}

// ── 4. process ────────────────────────────────────────────────────────────────

function playTick() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(900, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(500, ctx.currentTime + 0.05);
    gain.gain.setValueAtTime(0.028, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.08);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.08);
  } catch (_) {}
}

function ProcessSlide({ slide, bgUrl, videoUrl, altLogoUrl, qrUrl, qrLogoUrl, logoPosX = 36, interactive = false, textScale = 1 }) {
  const steps   = slide.steps || [];
  const n       = steps.length;
  const hasAny  = steps.some(s => s.subtitle);
  const [hovered, setHovered] = useState(-1);

  const numSize   = Math.round((n <= 3 ? 72 : n <= 5 ? 60 : 48) * textScale);
  const numW      = n <= 3 ? 96 : n <= 5 ? 82 : 66;
  const autoTitle = hasAny
    ? (n <= 3 ? 32 : n <= 5 ? 27 : 22)
    : (n <= 3 ? 42 : n <= 5 ? 34 : 26);
  const titleSize = Math.round((slide.fontSize ?? autoTitle) * textScale);
  const subSize   = Math.round((slide.fontSize ?? (n <= 3 ? 22 : n <= 5 ? 18 : 16)) * 0.62 * textScale);
  const rowGap    = hasAny
    ? Math.max(14, 40 - n * 4)
    : Math.max(18, 56 - n * 6);

  return (
    <Box sx={{ width: 1280, height: 720, position: 'relative', overflow: 'hidden', fontFamily }}>
      <BgImage url={bgUrl} videoUrl={videoUrl} theme="dark" />
      <Box sx={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }} />
      <Box sx={{
        position: 'absolute', left: 72, top: 0, bottom: 0,
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        gap: `${rowGap}px`, maxWidth: 900,
      }}>
        {slide.label && <Label sx={{ mb: 1 }}>{slide.label}</Label>}
        {steps.map((s, i) => {
          const pinned  = !!s.pinned;
          const isHover = interactive && hovered === i;
          const accent  = pinned || isHover;
          return (
            <Box
              key={i}
              sx={{
                display: 'flex', alignItems: 'center', gap: '28px',
                cursor: interactive ? 'default' : undefined,
                transition: 'opacity 0.18s',
                opacity: interactive && hovered !== -1 && !isHover && !pinned ? 0.45 : 1,
              }}
              onMouseEnter={interactive ? () => { setHovered(i); playTick(); } : undefined}
              onMouseLeave={interactive ? () => setHovered(-1) : undefined}
            >
              <Typography sx={{
                fontFamily, fontSize: numSize, fontWeight: 800,
                color: accent ? colors.white : colors.orange,
                lineHeight: 1, minWidth: numW, flexShrink: 0, textAlign: 'center',
                transition: 'all 0.18s',
                ...(accent ? {
                  bgcolor: colors.orange,
                  borderRadius: `${Math.round(numW * 0.18)}px`,
                  py: '6px',
                } : {}),
              }}>
                {s.number || String(i + 1).padStart(2, '0')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Typography sx={{
                  fontFamily, fontSize: titleSize, fontWeight: 700,
                  color: accent ? colors.orange : colors.white,
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                  lineHeight: 1.1, transition: 'color 0.18s',
                }}>
                  {s.title}
                </Typography>
                {s.subtitle && (
                  <Typography sx={{
                    fontFamily, fontSize: subSize, fontWeight: 400,
                    color: accent ? 'rgba(255,180,80,0.85)' : 'rgba(255,255,255,0.70)',
                    lineHeight: 1.4, transition: 'color 0.18s',
                  }}>
                    {s.subtitle}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
      <TikabLogo light altLogoUrl={altLogoUrl} qrUrl={qrUrl} qrLogoUrl={qrLogoUrl} logoPosX={logoPosX} />
    </Box>
  );
}

// ── 5. bullets ────────────────────────────────────────────────────────────────

function BulletsSlide({ slide, bgUrl, videoUrl, slideTheme, altLogoUrl, qrUrl, qrLogoUrl, logoPosX = 36, textScale = 1 }) {
  const bullets  = slide.bullets || [];
  const n        = bullets.length;
  // Auto-storlek — åsidosätts av slide.titleFontSize / slide.bulletFontSize
  const autoTitle  = n <= 3 ? 52 : n <= 4 ? 44 : n <= 6 ? 36 : 28;
  const autoBullet = n <= 3 ? 36 : n <= 4 ? 30 : n <= 6 ? 26 : 22;
  const maxTitle  = Math.round((slide.titleFontSize  ?? autoTitle) * textScale);
  const maxBullet = Math.round((slide.bulletFontSize ?? autoBullet) * textScale);
  const panelStyle = getPanelStyles(slideTheme, 'dark', { panelOpacity: slide.panelOpacity, panelBlur: slide.panelBlur });
  const pv = 52, ph = 56; // padding vertical/horisontell

  const titleRef   = useRef();
  const contentRef = useRef();
  const panelRef   = useRef();
  const titleSize  = useFitText(titleRef,   [slide.title, n, maxTitle],   maxTitle);
  const bulletSize = useFitText(contentRef, [bullets.join(''), n, maxBullet], maxBullet, 12);

  return (
    <Box sx={{ width: 1280, height: 720, position: 'relative', overflow: 'hidden', fontFamily }}>
      <BgImage url={bgUrl} videoUrl={videoUrl} theme={slide.theme || 'dark'} />
      {/* Glasspanel */}
      <Box ref={panelRef} sx={{
        position: 'absolute', left: 64, top: 64, bottom: 64, width: 660,
        ...panelStyle,
        p: `${pv}px ${ph}px`,
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        overflow: 'hidden',
      }}>
        {slide.label && <Label>{slide.label}</Label>}
        {slide.title && (
          <Box ref={titleRef} sx={{ overflow: 'hidden', flexShrink: 0 }}>
            <Typography sx={{
              fontFamily, fontSize: titleSize, fontWeight: 700,
              color: colors.white, mb: 2.5, lineHeight: 1.2,
            }}>
              {slide.title}
            </Typography>
          </Box>
        )}
        <Box
          ref={contentRef}
          component="ul"
          sx={{ m: 0, pl: 0, listStyle: 'none', overflow: 'hidden', flex: 1 }}
        >
          {bullets.map((b, i) => (
            <Box key={i} component="li" sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1.5 }}>
              <Box sx={{
                width: 7, height: 7, borderRadius: '50%',
                bgcolor: colors.orange, mt: `${bulletSize * 0.4}px`, flexShrink: 0,
              }} />
              <Typography sx={{
                fontFamily, fontSize: bulletSize, fontWeight: 400,
                color: 'rgba(255,255,255,0.9)', lineHeight: 1.45,
              }}>
                {b}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <TikabLogo light altLogoUrl={altLogoUrl} qrUrl={qrUrl} qrLogoUrl={qrLogoUrl} logoPosX={logoPosX} />
    </Box>
  );
}

// ── 6. key-statements ─────────────────────────────────────────────────────────

function KeyStatementsSlide({ slide, bgUrl, videoUrl, slideTheme, altLogoUrl, qrUrl, qrLogoUrl, logoPosX = 36, textScale = 1 }) {
  const statements  = slide.statements || [];
  const totalChars  = statements.join('').length;
  const autoSize    = totalChars < 60 ? 52 : totalChars < 120 ? 44 : totalChars < 200 ? 36 : 28;
  const maxSize     = Math.round((slide.fontSize ?? autoSize) * textScale);
  const panelStyle  = getPanelStyles(slideTheme, 'dark', { panelOpacity: slide.panelOpacity, panelBlur: slide.panelBlur });
  const contentRef  = useRef();
  const fontSize    = useFitText(contentRef, [statements.join(''), statements.length, maxSize], maxSize, 16);

  return (
    <Box sx={{ width: 1280, height: 720, position: 'relative', overflow: 'hidden', fontFamily }}>
      <BgImage url={bgUrl} videoUrl={videoUrl} theme={slide.theme || 'light'} />
      {/* Centrat glassblock */}
      <Box sx={{
        position: 'absolute',
        left: '50%', top: '50%',
        transform: 'translate(-50%, -50%)',
        width: 900,
        maxHeight: 620,
        ...panelStyle,
        p: '52px 68px',
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        overflow: 'hidden',
      }}>
        <Box
          ref={contentRef}
          sx={{ overflow: 'hidden' }}
        >
          {statements.map((s, i) => (
            <Typography key={i} sx={{
              fontFamily, fontSize, fontWeight: 800,
              color: colors.white, lineHeight: 1.3,
              borderBottom: i < statements.length - 1 ? '1px solid rgba(255,255,255,0.10)' : 'none',
              py: `${Math.max(8, 20 - statements.length * 2)}px`,
            }}>
              {s}
            </Typography>
          ))}
        </Box>
      </Box>
      <TikabLogo light={false} altLogoUrl={altLogoUrl} qrUrl={qrUrl} qrLogoUrl={qrLogoUrl} logoPosX={logoPosX} />
    </Box>
  );
}

// ── 7a. reference-single — ett projekt i helskärm ────────────────────────────

function ReferenceSingleSlide({ slide, bgUrl, videoUrl, slideTheme, altLogoUrl, qrUrl, qrLogoUrl, logoPosX = 36, textScale = 1 }) {
  const p          = slide.project || {};
  const th         = { ...DEFAULT_SLIDE_THEME, ...slideTheme };
  const effectiveBg = p.imageUrl || bgUrl;
  const panelStyle  = getPanelStyles(slideTheme, 'dark', { panelOpacity: slide.panelOpacity, panelBlur: slide.panelBlur });
  const descRef     = useRef();
  const maxDesc     = Math.round((slide.fontSize ?? 18) * textScale);
  // Vilken text visas i den flexibla textboxen?
  const mainText = th.refShowOurAssignment ? (p.ourAssignment || '') : th.refShowDescription ? (p.description || '') : '';
  const descSize = useFitText(descRef, [mainText, maxDesc], maxDesc, 11);

  const maxChips = slide.maxChips ?? 8;

  // Metadata-rad (budget + tid)
  const metaParts = [];
  if (th.refShowBudget && p.budget)    metaParts.push(p.budget);
  if (th.refShowTime   && (p.startDate || p.endDate)) {
    metaParts.push([p.startDate, p.endDate].filter(Boolean).join(' – '));
  }

  return (
    <Box sx={{ width: 1280, height: 720, position: 'relative', overflow: 'hidden', fontFamily }}>
      <BgImage url={effectiveBg} videoUrl={videoUrl} theme="dark" />
      <Box sx={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(90deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.30) 55%, rgba(0,0,0,0.0) 100%)',
      }} />
      {/* Textpanel vänster */}
      <Box sx={{
        position: 'absolute', left: 64, top: 64, bottom: 64, width: 580,
        ...panelStyle,
        p: '40px 48px',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {slide.label && <Label sx={{ mb: 1 }}>{slide.label}</Label>}
        <Typography sx={{
          fontFamily, fontSize: Math.round((p.title?.length > 30 ? 34 : 44) * textScale),
          fontWeight: 800, color: colors.white, lineHeight: 1.1, mb: 1,
          flexShrink: 0,
        }}>
          {p.title || 'Projektnamn'}
        </Typography>

        {/* Kund + metadata */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '12px', mb: 1.5, flexShrink: 0 }}>
          {p.client && (
            <Typography sx={{ fontFamily, fontSize: 16, fontWeight: 500, color: 'rgba(255,255,255,0.7)' }}>
              {p.client}
            </Typography>
          )}
          {metaParts.map((m, i) => (
            <Typography key={i} sx={{ fontFamily, fontSize: 14, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.02em' }}>
              {m}
            </Typography>
          ))}
        </Box>

        {/* Vår roll */}
        {th.refShowRole && p.role && (
          <Box sx={{ mb: 1.5, flexShrink: 0 }}>
            <Typography sx={{ fontFamily, fontSize: 10, fontWeight: 700, color: colors.orange,
              letterSpacing: '0.14em', textTransform: 'uppercase', mb: 0.4 }}>
              Vår roll
            </Typography>
            <Typography sx={{ fontFamily, fontSize: 14, color: 'rgba(255,255,255,0.8)', lineHeight: 1.4 }}>
              {p.role}
            </Typography>
          </Box>
        )}

        {/* Projektbeskrivning */}
        {th.refShowDescription && p.description && (
          <Box sx={{ mb: 1.5, flexShrink: 0 }}>
            <Typography sx={{ fontFamily, fontSize: 10, fontWeight: 700, color: colors.orange,
              letterSpacing: '0.14em', textTransform: 'uppercase', mb: 0.4 }}>
              Om projektet
            </Typography>
            <Typography sx={{ fontFamily, fontSize: 14, color: 'rgba(255,255,255,0.75)', lineHeight: 1.45,
              display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {p.description}
            </Typography>
          </Box>
        )}

        {/* Vårt uppdrag — flexibel textruta */}
        {th.refShowOurAssignment && p.ourAssignment && (
          <Box ref={descRef} sx={{ flex: 1, overflow: 'hidden', mb: 1 }}>
            <Typography sx={{ fontFamily, fontSize: 10, fontWeight: 700, color: colors.orange,
              letterSpacing: '0.14em', textTransform: 'uppercase', mb: 0.4 }}>
              Vårt uppdrag
            </Typography>
            <Typography sx={{ fontFamily, fontSize: descSize, color: 'rgba(255,255,255,0.85)', lineHeight: 1.5 }}>
              {p.ourAssignment}
            </Typography>
          </Box>
        )}

        {/* Nyckelord */}
        {th.refShowChips && (p.keywords?.length > 0) && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', mt: 'auto', flexShrink: 0 }}>
            {p.keywords.slice(0, maxChips).map((kw, i) => (
              <Chip key={i} label={kw} size="small" sx={{
                fontFamily, fontSize: 12, fontWeight: 600,
                bgcolor: `${colors.orange}20`, color: colors.orange,
                border: `1px solid ${colors.orange}55`,
                borderRadius: '4px', height: 24,
                '& .MuiChip-label': { px: '9px' },
              }} />
            ))}
          </Box>
        )}
      </Box>
      <TikabLogo light altLogoUrl={altLogoUrl} qrUrl={qrUrl} qrLogoUrl={qrLogoUrl} logoPosX={logoPosX} />
    </Box>
  );
}

// ── 7b. reference — flera projekt i kolumner (2–5) ───────────────────────────

function ReferenceSlide({ slide, bgUrl, videoUrl, slideTheme, altLogoUrl, qrUrl, qrLogoUrl, logoPosX = 36, textScale = 1 }) {
  const projects  = (slide.projects || []).filter(Boolean);
  const count     = Math.min(Math.max(projects.length, 1), 5);
  const cardStyle = getPanelStyles(slideTheme, 'dark');
  const radius    = slideTheme?.borderRadius ?? 14;
  const th        = { ...DEFAULT_SLIDE_THEME, ...slideTheme };
  const maxChips  = slide.maxChips ?? (count <= 2 ? 6 : count === 3 ? 4 : 3);

  // Textstorlekar efter antal projekt
  const imgH      = count <= 2 ? 200 : count === 3 ? 160 : count === 4 ? 130 : 90;
  const autoTitle = count <= 2 ? 20  : count === 3 ? 17  : 14;
  const autoDesc  = count <= 2 ? 14  : count === 3 ? 12  : 11;
  const titleSize = Math.round((slide.fontSize ?? autoTitle) * textScale);
  const descSize  = Math.round((slide.fontSize ? Math.round(slide.fontSize * 0.72) : autoDesc) * textScale);
  const descLines = count <= 2 ? 4   : count === 3 ? 3   : 2;
  const pad       = count <= 2 ? 18  : 12;

  return (
    <Box sx={{ width: 1280, height: 720, position: 'relative', overflow: 'hidden', fontFamily }}>
      <BgImage url={bgUrl} videoUrl={videoUrl} theme="dark" />
      <Box sx={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.40)' }} />
      <Box sx={{ position: 'absolute', top: 48, left: 72 }}>
        {slide.label && <Label>{slide.label}</Label>}
        {slide.title && (
          <Typography sx={{ fontFamily, fontSize: 28, fontWeight: 700, color: colors.white, maxWidth: 900, lineHeight: 1.2 }}>
            {slide.title}
          </Typography>
        )}
      </Box>
      <Box sx={{
        position: 'absolute',
        top: (slide.label || slide.title) ? 136 : 48,
        bottom: 48, left: 72, right: 72,
        display: 'flex', gap: `${slide.cardGap ?? 12}px`,
      }}>
        {projects.slice(0, 5).map((p, i) => (
          <Box key={i} sx={{ flex: 1, ...cardStyle, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {(p.imageUrl || p.image) && (
              <Box sx={{
                width: '100%', height: imgH, flexShrink: 0,
                backgroundImage: `url("${p.imageUrl || p.image}")`,
                backgroundSize: 'cover', backgroundPosition: 'center',
                borderRadius: `${radius}px ${radius}px 0 0`,
              }} />
            )}
            <Box sx={{ p: `${pad}px`, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <Typography sx={{ fontFamily, fontSize: titleSize, fontWeight: 700, color: colors.white, lineHeight: 1.2 }}>
                {p.title}
              </Typography>
              {th.refShowRole && p.role && (
                <Box>
                  <Typography sx={{ fontFamily, fontSize: 8, fontWeight: 700, color: colors.orange,
                    letterSpacing: '0.12em', textTransform: 'uppercase', mb: '2px' }}>
                    Vår roll
                  </Typography>
                  <Typography sx={{ fontFamily, fontSize: autoDesc, fontWeight: 500,
                    color: 'rgba(255,255,255,0.80)', lineHeight: 1.3 }}>
                    {p.role}
                  </Typography>
                </Box>
              )}
              {(th.refShowDescription && p.description) && (
                <Box sx={{ flex: th.refShowOurAssignment ? undefined : 1, overflow: 'hidden' }}>
                  <Typography sx={{ fontFamily, fontSize: 8, fontWeight: 700, color: colors.orange,
                    letterSpacing: '0.12em', textTransform: 'uppercase', mb: '2px' }}>
                    Om projektet
                  </Typography>
                  <Typography sx={{
                    fontFamily, fontSize: descSize, color: 'rgba(255,255,255,0.60)', lineHeight: 1.35,
                    display: '-webkit-box', WebkitLineClamp: descLines,
                    WebkitBoxOrient: 'vertical', overflow: 'hidden',
                  }}>
                    {p.description}
                  </Typography>
                </Box>
              )}
              {(th.refShowOurAssignment && p.ourAssignment) && (
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                  <Typography sx={{ fontFamily, fontSize: 8, fontWeight: 700, color: colors.orange,
                    letterSpacing: '0.12em', textTransform: 'uppercase', mb: '2px' }}>
                    Uppdragsbeskrivning
                  </Typography>
                  <Typography sx={{
                    fontFamily, fontSize: descSize, color: 'rgba(255,255,255,0.75)', lineHeight: 1.35,
                    display: '-webkit-box', WebkitLineClamp: descLines,
                    WebkitBoxOrient: 'vertical', overflow: 'hidden',
                  }}>
                    {p.ourAssignment}
                  </Typography>
                </Box>
              )}
              {(th.refShowBudget && p.budget) && (
                <Typography sx={{ fontFamily, fontSize: autoDesc - 1, color: 'rgba(255,255,255,0.50)' }}>
                  {p.budget}
                </Typography>
              )}
              {(th.refShowTime && (p.startDate || p.endDate)) && (
                <Typography sx={{ fontFamily, fontSize: autoDesc - 1, color: 'rgba(255,255,255,0.50)' }}>
                  {[p.startDate, p.endDate].filter(Boolean).join(' – ')}
                </Typography>
              )}
              {th.refShowChips && (p.keywords?.length > 0) && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', mt: 'auto' }}>
                  {p.keywords.slice(0, maxChips).map((kw, j) => (
                    <Chip key={j} label={kw} size="small" sx={{
                      fontFamily, fontSize: count <= 3 ? 11 : 9, fontWeight: 600,
                      bgcolor: `${colors.orange}18`, color: colors.orange,
                      border: `1px solid ${colors.orange}44`,
                      borderRadius: '3px', height: count <= 3 ? 20 : 16,
                      '& .MuiChip-label': { px: count <= 3 ? '7px' : '5px' },
                    }} />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        ))}
      </Box>
      <TikabLogo light altLogoUrl={altLogoUrl} qrUrl={qrUrl} qrLogoUrl={qrLogoUrl} logoPosX={logoPosX} />
    </Box>
  );
}

// ── 8. closing ────────────────────────────────────────────────────────────────

function ClosingSlide({ slide, bgUrl, videoUrl, slideTheme, altLogoUrl, qrUrl, qrLogoUrl, logoPosX = 36, textScale = 1 }) {
  const cta      = slide.callToAction || '';
  const autoCTA  = cta.length < 30 ? 72 : cta.length < 50 ? 58 : 44;
  const maxCTA   = Math.round((slide.fontSize ?? autoCTA) * textScale);
  const panelStyle = getPanelStyles(slideTheme, 'dark', { panelOpacity: slide.panelOpacity, panelBlur: slide.panelBlur });
  const ctaRef   = useRef();
  const ctaSize  = useFitText(ctaRef, [cta, maxCTA], maxCTA, 24);

  return (
    <Box sx={{ width: 1280, height: 720, position: 'relative', overflow: 'hidden', fontFamily }}>
      <BgImage url={bgUrl} videoUrl={videoUrl} theme={slide.theme || 'dark'} />
      <Box sx={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'flex-start', justifyContent: 'center',
        px: '72px',
      }}>
        {slide.label && (
          <Typography sx={{
            fontFamily, fontSize: 18, fontWeight: 700,
            color: colors.orange, letterSpacing: '0.15em',
            textTransform: 'uppercase', mb: 2,
          }}>
            {slide.label}
          </Typography>
        )}
        {cta && (
          <Box sx={{ ...panelStyle, px: '52px', py: '40px', maxWidth: 900 }}>
            <Box ref={ctaRef} sx={{ overflow: 'hidden' }}>
              <Typography sx={{
                fontFamily, fontSize: ctaSize, fontWeight: 700,
                fontStyle: 'italic', color: colors.white, lineHeight: 1.15,
              }}>
                {cta}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
      <TikabLogo light altLogoUrl={altLogoUrl} qrUrl={qrUrl} qrLogoUrl={qrLogoUrl} logoPosX={logoPosX} />
    </Box>
  );
}

// ── 9. agenda ─────────────────────────────────────────────────────────────────

function AgendaSlide({ slide, bgUrl, videoUrl, slideTheme, altLogoUrl, qrUrl, qrLogoUrl, logoPosX = 36, interactive = false, textScale = 1 }) {
  const items      = slide.items || [];
  const n          = items.length;
  const [hovered, setHovered] = useState(-1);
  const panelStyle = getPanelStyles(slideTheme, 'dark', { panelOpacity: slide.panelOpacity, panelBlur: slide.panelBlur });
  const autoDesc   = n <= 4 ? 30 : n <= 6 ? 26 : 21;
  const descSize   = Math.round((slide.fontSize ?? autoDesc) * textScale);
  const timeSize   = Math.round(descSize * 0.84);
  const rowGap     = n <= 3 ? 36 : n <= 5 ? 26 : n <= 7 ? 18 : 12;
  const titleW     = slide.titleColW ?? Math.round(1280 * 0.28);

  return (
    <Box sx={{ width: 1280, height: 720, position: 'relative', overflow: 'hidden', fontFamily }}>
      <BgImage url={bgUrl} videoUrl={videoUrl} theme={slide.theme || 'dark'} />

      <Box sx={{
        position: 'absolute', left: 0, right: 0, top: 116, bottom: 68,
        ...panelStyle, borderRadius: 0,
        display: 'flex', flexDirection: 'row', overflow: 'hidden',
      }}>
        {/* Vänster — rubrikdel */}
        <Box sx={{
          width: titleW, flexShrink: 0,
          borderRight: '1px solid rgba(255,255,255,0.12)',
          px: '44px', py: '32px',
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
        }}>
          {slide.label && (
            <Typography sx={{
              fontFamily, fontSize: 13, fontWeight: 700, color: colors.orange,
              letterSpacing: '0.16em', textTransform: 'uppercase', mb: 1.5,
            }}>
              {slide.label}
            </Typography>
          )}
          <Typography sx={{
            fontFamily, fontSize: Math.round((slide.titleFontSize ?? 72) * textScale), fontWeight: 800,
            color: colors.white, lineHeight: 1.0, overflowWrap: 'break-word',
          }}>
            {slide.title || 'Agenda'}
          </Typography>
          {/* Datum — tätt under titeln, högerjusterat */}
          {slide.date && (
            <Typography sx={{
              fontFamily, fontSize: 15, fontWeight: 500,
              color: 'rgba(255,255,255,0.50)', mt: '6px',
              textAlign: 'right', letterSpacing: '0.03em',
            }}>
              {slide.date}
            </Typography>
          )}
          {slide.subtitle && (
            <Typography sx={{
              fontFamily, fontSize: 18, fontWeight: 400,
              color: 'rgba(255,255,255,0.55)', mt: 2, lineHeight: 1.4,
              overflowWrap: 'break-word',
            }}>
              {slide.subtitle}
            </Typography>
          )}
        </Box>

        {/* Höger — körschema */}
        <Box sx={{
          flex: 1, px: '52px', py: '32px',
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
          gap: `${rowGap}px`, overflow: 'hidden',
        }}>
          {items.map((item, i) => {
            const isHover = interactive && hovered === i;
            return (
              <Box
                key={i}
                sx={{
                  display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'nowrap',
                  transition: 'opacity 0.15s',
                  opacity: interactive && hovered !== -1 && !isHover ? 0.4 : 1,
                  cursor: interactive ? 'default' : undefined,
                }}
                onMouseEnter={interactive ? () => { setHovered(i); playTick(); } : undefined}
                onMouseLeave={interactive ? () => setHovered(-1) : undefined}
              >
                {/* Tid */}
                <Typography sx={{
                  fontFamily, fontSize: timeSize, fontWeight: 700,
                  color: colors.orange,
                  width: 90, flexShrink: 0, letterSpacing: '0.04em',
                }}>
                  {item.time || ''}
                </Typography>
                {/* Aktivitet + beskrivning */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{
                    fontFamily, fontSize: descSize, fontWeight: 600,
                    color: isHover ? colors.orange : colors.white,
                    lineHeight: 1.2, transition: 'color 0.15s',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {item.title}
                  </Typography>
                  {item.description && (
                    <Typography sx={{
                      fontFamily, fontSize: Math.max(15, descSize - 6), fontWeight: 400,
                      color: isHover ? 'rgba(255,180,80,0.85)' : 'rgba(255,255,255,0.55)',
                      lineHeight: 1.3, mt: '3px', transition: 'color 0.15s',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {item.description}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      <TikabLogo light altLogoUrl={altLogoUrl} qrUrl={qrUrl} qrLogoUrl={qrLogoUrl} logoPosX={logoPosX} />
    </Box>
  );
}

// ── 10. image / video / embed ─────────────────────────────────────────────────

function toEmbedUrl(url) {
  if (!url) return '';
  // YouTube: watch?v= eller youtu.be/
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  if (yt) {
    return `https://www.youtube.com/embed/${yt[1]}?autoplay=1&mute=1&loop=1&playlist=${yt[1]}&controls=0&rel=0&modestbranding=1`;
  }
  // Vimeo: vimeo.com/ID
  const vi = url.match(/vimeo\.com\/(\d+)/);
  if (vi) {
    return `https://player.vimeo.com/video/${vi[1]}?autoplay=1&loop=1&muted=1&background=1`;
  }
  return url; // generisk iframe
}

function ImageSlide({ slide, bgUrl, videoUrl, altLogoUrl, qrUrl, qrLogoUrl, logoPosX = 36, textScale: _textScale = 1 }) {
  const mediaType = slide.mediaType || 'image';
  const scale     = (slide.imageScale ?? 100) / 100;
  const offsetX   = slide.imageOffsetX ?? 0;
  const offsetY   = slide.imageOffsetY ?? 0;

  const cachedVideo = useCachedVideo(mediaType === 'video' ? (slide.videoUrl || '') : '');
  const embedUrl    = mediaType === 'embed' ? toEmbedUrl(slide.webVideoUrl || '') : '';

  const mediaTransform = {
    position: 'absolute',
    top: '50%', left: '50%',
    transform: `translate(calc(-50% + ${offsetX}px), calc(-50% + ${offsetY}px)) scale(${scale})`,
    transformOrigin: 'center center',
  };

  return (
    <Box sx={{ width: 1280, height: 720, position: 'relative', overflow: 'hidden', fontFamily }}>
      <BgImage url={bgUrl} videoUrl={videoUrl} theme={slide.theme || 'dark'} />

      {/* Bild */}
      {mediaType === 'image' && slide.imageUrl && (
        <Box component="img" src={slide.imageUrl} alt=""
          sx={{ ...mediaTransform, maxWidth: 1280, maxHeight: 720, objectFit: 'contain', pointerEvents: 'none' }} />
      )}

      {/* Uppladdad video */}
      {mediaType === 'video' && slide.videoUrl && (
        <Box component="video"
          src={cachedVideo || slide.videoUrl}
          autoPlay muted loop playsInline preload="auto"
          sx={{ ...mediaTransform, width: 1280, height: 720, objectFit: 'contain' }} />
      )}

      {/* Webblänk — YouTube / Vimeo / generisk iframe */}
      {mediaType === 'embed' && embedUrl && (
        <>
          <Box component="iframe"
            src={embedUrl}
            allow="autoplay; fullscreen"
            frameBorder="0"
            title="video"
            sx={{ ...mediaTransform, width: 1280, height: 720, border: 'none', display: 'block' }} />
          {/* Transparent overlay — stoppar klick på iframe inuti preview */}
          <Box sx={{ position: 'absolute', inset: 0, zIndex: 5 }} />
        </>
      )}

      <TikabLogo light altLogoUrl={altLogoUrl} qrUrl={qrUrl} qrLogoUrl={qrLogoUrl} logoPosX={logoPosX} />
    </Box>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SlideRenderer
// ══════════════════════════════════════════════════════════════════════════════

export default function SlideRenderer({ slide, backgrounds = [], slideTheme, altLogoUrl, qrUrl, qrLogoUrl, showNotes = false, fitHeight = false, interactive = false }) {
  const containerRef = useRef();
  const scale        = useSlideScale(containerRef, fitHeight);
  const textScale    = (scale > 0 && scale < 0.42) ? 0.82 : 1;

  const bgEntry  = backgrounds.find(b => b.id === slide?.backgroundImage?.id);
  // Uppladdad anpassad bakgrund har prioritet; annars bibliotekets bild/video
  const bgUrl    = slide?.customBgUrl || (bgEntry?.isVideo ? '' : (bgEntry?.url || ''));
  const videoUrl = slide?.customBgUrl ? '' : (bgEntry?.isVideo ? (bgEntry?.url || '') : (slide?.backgroundVideo?.url || ''));

  function renderSlide() {
    const type  = slide?.type || 'cover';
    const logoPosX = slideTheme?.logoPosX ?? 36;
    const props = { slide, bgUrl, videoUrl, slideTheme, altLogoUrl, qrUrl, qrLogoUrl, logoPosX, interactive, textScale };
    switch (type) {
      case 'cover':             return <CoverSlide {...props} />;
      case 'hero':              return <HeroSlide {...props} />;
      case 'hero-mirror':       return <HeroMirrorSlide {...props} />;
      case 'divider':           return <DividerSlide {...props} />;
      case 'section':           return <SectionSlide {...props} />;
      case 'stats':             return <StatsSlide {...props} />;
      case 'process':           return <ProcessSlide {...props} />;
      case 'bullets':           return <BulletsSlide {...props} />;
      case 'key-statements':    return <KeyStatementsSlide {...props} />;
      case 'reference':         return <ReferenceSlide {...props} />;
      case 'reference-single':  return <ReferenceSingleSlide {...props} />;
      case 'closing':           return <ClosingSlide {...props} />;
      case 'agenda':            return <AgendaSlide {...props} />;
      case 'image':             return <ImageSlide {...props} />;
      default:                  return <DividerSlide {...props} />;
    }
  }

  // Centered offsets when fitHeight — center the scaled 1280×720 box in the container
  const scaledW = 1280 * scale;
  const scaledH = 720  * scale;

  return (
    <Box
      ref={containerRef}
      sx={{
        width:  '100%',
        height: fitHeight ? '100%' : undefined,
        aspectRatio: fitHeight ? undefined : '16/9',
        overflow: 'hidden', position: 'relative',
        borderRadius: 1, bgcolor: colors.black,
        display: fitHeight ? 'flex' : undefined,
        alignItems: fitHeight ? 'center' : undefined,
        justifyContent: fitHeight ? 'center' : undefined,
      }}
    >
      {scale > 0 && (
        <Box sx={{
          width: 1280, height: 720,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          position: 'absolute',
          top:  fitHeight ? `calc(50% - ${scaledH / 2}px)` : 0,
          left: fitHeight ? `calc(50% - ${scaledW / 2}px)` : 0,
        }}>
          {renderSlide()}
        </Box>
      )}
    </Box>
  );
}
